<template>
  <div class="yoy-income-widget-container">
    <div v-if="loading" class="yoy-income-widget-loading">Loading...</div>
    <div v-else-if="error" class="yoy-income-widget-error">{{ error }}</div>
    <div v-else class="yoy-income-widget-content">
      <div class="yoy-income-widget-summary">
        <div class="yoy-income-widget-summary-item">
          <div class="yoy-income-widget-year-label">{{ previousYear }}</div>
          <div class="yoy-income-widget-year-value">{{ formatCurrency(previousYearTotal) }}</div>
        </div>
        <div class="yoy-income-widget-summary-item">
          <div class="yoy-income-widget-year-label">{{ currentYear }}</div>
          <div class="yoy-income-widget-year-value yoy-income-widget-current">{{ formatCurrency(currentYearTotal) }}</div>
        </div>
      </div>
      <div class="yoy-income-widget-change" :class="changeClass">
        <span class="yoy-income-widget-change-label">{{ changeLabel }}</span>
        <span class="yoy-income-widget-change-value">{{ changePercentage }}%</span>
      </div>
      <div class="yoy-income-widget-chart">
        <div class="yoy-income-widget-chart-bars">
          <div 
            v-for="(month, index) in months" 
            :key="index"
            class="yoy-income-widget-chart-month"
          >
            <div class="yoy-income-widget-chart-month-label">{{ month }}</div>
            <div class="yoy-income-widget-chart-bars-container">
              <div 
                class="yoy-income-widget-chart-bar yoy-income-widget-chart-bar-previous"
                :style="{ height: getBarHeight(previousYearData[index]) + '%' }"
                :title="formatCurrency(previousYearData[index])"
              ></div>
              <div 
                class="yoy-income-widget-chart-bar yoy-income-widget-chart-bar-current"
                :style="{ height: getBarHeight(currentYearData[index]) + '%' }"
                :title="formatCurrency(currentYearData[index])"
              ></div>
            </div>
          </div>
        </div>
        <div class="yoy-income-widget-chart-legend">
          <div class="yoy-income-widget-legend-item">
            <span class="yoy-income-widget-legend-color yoy-income-widget-legend-previous"></span>
            <span class="yoy-income-widget-legend-text">{{ previousYear }}</span>
          </div>
          <div class="yoy-income-widget-legend-item">
            <span class="yoy-income-widget-legend-color yoy-income-widget-legend-current"></span>
            <span class="yoy-income-widget-legend-text">{{ currentYear }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/YearOverYearIncomeWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'YearOverYearIncomeWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const previousYear = currentYear - 1;
    
    return {
      currentYear,
      previousYear,
      currentYearData: new Array(12).fill(0),
      previousYearData: new Array(12).fill(0),
      loading: true,
      error: null
    };
  },
  computed: {
    currentYearTotal() {
      return this.currentYearData.reduce((sum, val) => sum + val, 0);
    },
    previousYearTotal() {
      return this.previousYearData.reduce((sum, val) => sum + val, 0);
    },
    changePercentage() {
      if (this.previousYearTotal === 0) return this.currentYearTotal > 0 ? 100 : 0;
      const change = ((this.currentYearTotal - this.previousYearTotal) / this.previousYearTotal) * 100;
      return Math.abs(change).toFixed(1);
    },
    changeLabel() {
      if (this.currentYearTotal > this.previousYearTotal) return 'Increase';
      if (this.currentYearTotal < this.previousYearTotal) return 'Decrease';
      return 'No Change';
    },
    changeClass() {
      if (this.currentYearTotal > this.previousYearTotal) return 'yoy-income-widget-change-increase';
      if (this.currentYearTotal < this.previousYearTotal) return 'yoy-income-widget-change-decrease';
      return 'yoy-income-widget-change-neutral';
    },
    months() {
      return ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
    },
    maxValue() {
      const allValues = [...this.currentYearData, ...this.previousYearData];
      return Math.max(...allValues, 1); // At least 1 to avoid division by zero
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        // Get current year date range
        const currentYearStart = new Date(this.currentYear, 0, 1);
        const currentYearEnd = new Date(this.currentYear, 11, 31, 23, 59, 59);
        
        // Get previous year date range
        const previousYearStart = new Date(this.previousYear, 0, 1);
        const previousYearEnd = new Date(this.previousYear, 11, 31, 23, 59, 59);
        
        // Fetch invoices for both years
        const [currentYearInvoices, previousYearInvoices] = await Promise.all([
          this.fetchInvoicesForYear(currentYearStart, currentYearEnd),
          this.fetchInvoicesForYear(previousYearStart, previousYearEnd)
        ]);
        
        // Process current year data
        this.currentYearData = this.processMonthlyData(currentYearInvoices, this.currentYear);
        
        // Process previous year data
        this.previousYearData = this.processMonthlyData(previousYearInvoices, this.previousYear);
      } catch (error) {
        logger.error('Error fetching year over year income data:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    async fetchInvoicesForYear(startDate, endDate) {
      try {
        const invoices = await ApiService.getAllCustomerInvoices(this.companyId);
        const invoicesArray = Array.isArray(invoices) ? invoices : [];
        
        // Filter invoices within date range
        return invoicesArray.filter(invoice => {
          if (!invoice.date) return false;
          const invoiceDate = new Date(invoice.date);
          return invoiceDate >= startDate && invoiceDate <= endDate && 
                 invoice.status !== 'draft' && 
                 invoice.status !== 'cancelled' &&
                 invoice.status !== 'voided';
        });
      } catch (error) {
        logger.error('Error fetching invoices for year:', error);
        return [];
      }
    },
    processMonthlyData(invoices, year) {
      const monthlyData = new Array(12).fill(0);
      
      invoices.forEach(invoice => {
        if (!invoice.date) return;
        const invoiceDate = new Date(invoice.date);
        const month = invoiceDate.getMonth(); // 0-11
        
        if (invoiceDate.getFullYear() === year && month >= 0 && month < 12) {
          monthlyData[month] += parseFloat(invoice.totalAmount || 0);
        }
      });
      
      return monthlyData;
    },
    getBarHeight(value) {
      if (this.maxValue === 0) return 0;
      return Math.max((value / this.maxValue) * 100, 2); // Minimum 2% for visibility
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

